// src/components/blog/BlogPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Sanity
import sanityClient from '../../config/sanityClient';

// Firebase Imports
import { logEvent } from 'firebase/analytics';
import { analytics, auth } from '../../firebase/firebase';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';

// Component Imports
import AuthModal from '../auth/AuthModal';

// Icon Imports
import { Menu, X } from 'lucide-react';

const googleProvider = new GoogleAuthProvider();

const BlogPage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // --- Modal logic ---
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tptStoreUrlInput, setTptStoreUrlInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  const openAuthModal = (mode = 'signup') => {
    setAuthError('');
    setResetMessage('');
    setShowForgotPassword(false);
    setAuthMode(mode);
    setIsAuthModalOpen(true);
  };

  const handleAuthAction = async (mode) => {
    setAuthError('');
    try {
      if (mode === 'signup') {
        await createUserWithEmailAndPassword(auth, email, password);
        logEvent(analytics, 'sign_up', { method: 'Email' });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        logEvent(analytics, 'login', { method: 'Email' });
      }
      setIsAuthModalOpen(false);
      navigate('/app');
    } catch (error) {
      setAuthError(error.message.replace('Firebase: ', ''));
    }
  };

  const handleGoogleSignIn = async () => {
    setAuthError('');
    try {
      await signInWithPopup(auth, googleProvider);
      logEvent(analytics, 'sign_up', { method: 'Google' });
      setIsAuthModalOpen(false);
      navigate('/app');
    } catch (error) {
      setAuthError(error.message.replace('Firebase: ', ''));
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setAuthError('');
    setResetMessage('');
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage('Password reset email sent! Please check your inbox.');
    } catch (error) {
      setAuthError(error.message.replace('Firebase: ', ''));
    }
  };

  const navLinks = [
    { href: '/#features', label: 'Features' },
    { href: '/#how-to-use', label: 'How It Works' },
    { href: '/#pricing', label: 'Pricing' },
    { href: '/how-to-use', label: 'User Guide' },
    { href: '/blog', label: 'Blog' },
    { href: '/#faq', label: 'FAQ' },
  ];

  // --- Blog listing state ---
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [postsError, setPostsError] = useState('');

  useEffect(() => {
    // GROQ: adjust fields to match your schema
    const query = `
      *[_type == "post"] | order(publishedAt desc) {
        title,
        "slug": slug.current,
        publishedAt,
        excerpt,
        mainImage{
          asset->{url, metadata{lqip}}
        }
      }
    `;
    sanityClient
      .fetch(query)
      .then((data) => setPosts(data || []))
      .catch((err) => setPostsError(err.message))
      .finally(() => setLoadingPosts(false));
  }, []);

  return (
    <>
      <Helmet>
        <title>TPT Seller Blog | Tips to Improve SEO, Descriptions & Sales</title>
        <meta
          name="description"
          content="Actionable TPT seller tips to boost product rankings, improve descriptions, and drive more sales. Learn how to master SEO and stand out on Teachers Pay Teachers."
        />
        <meta name="robots" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:title" content="TPT Seller Blog | SEO & Sales Tips" />
        <meta
          property="og:description"
          content="Learn how to boost your product rankings, improve your TPT listings, and grow your sales with expert SEO tips."
        />
        <meta property="og:url" content="https://mrjoelstptgenerator.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://mrjoelstptgenerator.com/blog-og.jpg" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TPT Seller Blog | SEO & Sales Tips" />
        <meta
          name="twitter:description"
          content="Boost your TPT store’s visibility and sales with proven SEO and marketing strategies."
        />
        <meta name="twitter:image" content="https://mrjoelstptgenerator.com/blog-og.jpg" />
      </Helmet>

      <div className="bg-slate-50 text-slate-900 min-h-screen flex flex-col">
        <header className="bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex-shrink-0">
                <Link to="/" className="text-xl font-bold text-sky-800">Mr Joel's Seller Tools</Link>
              </div>
              <nav className="hidden md:flex items-center space-x-2">
                {navLinks.map((link) =>
                  link.href === '/blog' ? (
                    <Link key={link.href} to={link.href} className="font-bold text-sky-600 bg-sky-100 transition-colors px-3 py-2 rounded-md">
                      {link.label}
                    </Link>
                  ) : (
                    <a key={link.href} href={link.href} className="font-medium text-slate-600 hover:text-sky-600 transition-colors px-3 py-2 rounded-md">
                      {link.label}
                    </a>
                  )
                )}
                <button
                  onClick={() => openAuthModal('signup')}
                  className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 px-4 rounded-full text-sm shadow-lg transition-transform transform hover:scale-105"
                >
                  Sign Up Free
                </button>
                <a
                  href="/app"
                  className="text-slate-600 hover:text-sky-600 font-medium py-2 px-4 rounded-full text-sm"
                >
                  Login
                </a>
              </nav>
              <div className="md:hidden flex items-center">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="inline-flex items-center justify-center p-2 rounded-md text-slate-700"
                  aria-expanded={isMenuOpen}
                >
                  {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>
          </div>
          {isMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                {navLinks.map((link) =>
                  link.href === '/blog' ? (
                    <Link key={link.href} to={link.href} onClick={() => setIsMenuOpen(false)} className="font-bold text-sky-600 bg-sky-100 block px-3 py-2 rounded-md text-base">
                      {link.label}
                    </Link>
                  ) : (
                    <a key={link.href} href={link.href} onClick={() => setIsMenuOpen(false)} className="text-slate-600 hover:bg-sky-50 hover:text-sky-700 block px-3 py-2 rounded-md text-base font-medium">
                      {link.label}
                    </a>
                  )
                )}
                <button onClick={() => { openAuthModal('signup'); setIsMenuOpen(false); }} className="w-full bg-sky-100 text-sky-700 block px-3 py-3 rounded-md text-base font-bold text-center mt-4">
                  Sign Up for Free
                </button>
                <a href="/app" className="w-full text-center block py-2 text-slate-600 font-medium">
                  Login
                </a>
              </div>
            </div>
          )}
        </header>

        {/* Main Blog Section */}
        <main className="flex-grow py-16 sm:py-24 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-sky-800 tracking-tight text-center mb-12">
              TPT Seller Blog
            </h1>

            {loadingPosts && (
              <p className="text-center text-slate-500">Loading posts…</p>
            )}

            {!loadingPosts && postsError && (
              <p className="text-center text-red-600">Error loading posts: {postsError}</p>
            )}

            {!loadingPosts && !postsError && posts.length === 0 && (
              <p className="text-center text-slate-500">No blog posts available yet.</p>
            )}

            {!loadingPosts && !postsError && posts.length > 0 && (
              <ul className="grid gap-6 md:grid-cols-2">
                {posts.map((post) => (
                  <li key={post.slug} className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden hover:shadow transition">
                    {post?.mainImage?.asset?.url && (
                      <Link to={`/blog/${post.slug}`} aria-label={`Read ${post.title}`}>
                        <img
                          src={post.mainImage.asset.url}
                          alt={post.title}
                          className="w-full h-48 object-cover"
                          loading="lazy"
                        />
                      </Link>
                    )}
                    <div className="p-6">
                      <Link to={`/blog/${post.slug}`} className="text-2xl font-semibold text-sky-700 hover:underline">
                        {post.title}
                      </Link>
                      {post.publishedAt && (
                        <p className="text-sm text-slate-500 mt-2">
                          Published on {new Date(post.publishedAt).toLocaleDateString()}
                        </p>
                      )}
                      {post.excerpt && (
                        <p className="mt-3 text-slate-700 line-clamp-3">{post.excerpt}</p>
                      )}
                      <div className="mt-4">
                        <Link
                          to={`/blog/${post.slug}`}
                          className="inline-block text-sky-700 font-medium hover:underline"
                        >
                          Read more →
                        </Link>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </main>

        <footer className="mt-24 bg-slate-800 text-slate-400 py-12">
          <div className="max-w-5xl mx-auto text-center px-4">
            <p>&copy; 2025 Mr. Joel's Seller Tools. All Rights Reserved.</p>
            <div className="mt-4 space-x-4">
              <a href="#" className="hover:text-white">Contact</a>
              <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white">Terms of Service</Link>
            </div>
          </div>
        </footer>
      </div>

      {/* AuthModal component */}
      {isAuthModalOpen && (
        <AuthModal
          onClose={() => setIsAuthModalOpen(false)}
          authMode={authMode}
          setAuthMode={setAuthMode}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          authError={authError}
          setAuthError={setAuthError}
          showForgotPassword={showForgotPassword}
          setShowForgotPassword={setShowForgotPassword}
          resetEmail={resetEmail}
          setResetEmail={setResetEmail}
          resetMessage={resetMessage}
          setResetMessage={setResetMessage}
          tptStoreUrlInput={tptStoreUrlInput}
          setTptStoreUrlInput={setTptStoreUrlInput}
          handleAuthAction={handleAuthAction}
          handlePasswordReset={handlePasswordReset}
          handleGoogleSignIn={handleGoogleSignIn}
        />
      )}
    </>
  );
};

export default BlogPage;
